import { useEffect, useRef, useState } from 'react';
import { ArrowLeftIcon, CheckIcon, PlugIcon } from 'lucide-react';
import { formatCopy, useNamespaceCopy } from '@/i18n/ns';
import welcomeCopy, { type WelcomeCopy } from '@/i18n/ns/welcome';
import { EntryButton } from '@/components/auth/entry-shell';
import { useConnections } from '@/workspace/use-connections';
import { completeCompanyWelcome, type CompanyContext } from '../api';
import { useCompany } from '../context';
import { ConnectTools } from './connect-tools';
import { FirstStep } from './first-step';
import {
  focuses,
  roles,
  readSetup,
  saveSetup,
  setupKey,
  type SetupPreferences,
} from './preferences';

const STEPS = 3;

function stepText(
  copy: WelcomeCopy,
  step: number,
  reminder: boolean,
  connected: number,
  name?: string,
) {
  if (reminder)
    return connected
      ? { title: copy.reminderDoneTitle, intro: copy.reminderDoneIntro }
      : { title: copy.reminderTitle, intro: copy.reminderIntro };
  if (step === 0)
    return {
      title: name ? formatCopy(copy.focusTitleNamed, { name }) : copy.focusTitle,
      intro: copy.focusIntro,
    };
  if (step === 1) return { title: copy.toolsTitle, intro: copy.toolsIntro };
  return { title: copy.firstTitle, intro: copy.firstIntro };
}

function Profile({
  copy,
  preferences,
  update,
}: {
  copy: WelcomeCopy;
  preferences: SetupPreferences;
  update: (patch: Partial<SetupPreferences>) => void;
}) {
  return (
    <div className="onboarding-profile">
      <section className="onboarding-profile-section" aria-labelledby="onboarding-role-heading">
        <h2 id="onboarding-role-heading">{copy.roleHeading}</h2>
        <div className="onboarding-roles" role="group" aria-labelledby="onboarding-role-heading">
          {roles.map((role) => (
            <button
              key={role.id}
              type="button"
              aria-pressed={preferences.role === role.id}
              onClick={() => update({ role: preferences.role === role.id ? null : role.id })}
            >
              {copy[role.label]}
            </button>
          ))}
        </div>
      </section>
      <section className="onboarding-profile-section" aria-labelledby="onboarding-goals-heading">
        <h2 id="onboarding-goals-heading">{copy.goalsHeading}</h2>
        <div className="onboarding-focus" role="group" aria-labelledby="onboarding-goals-heading">
          {focuses.map((focus) => {
            const selected = preferences.interests.includes(focus.id);
            return (
              <button
                key={focus.id}
                type="button"
                aria-pressed={selected}
                onClick={() =>
                  update({
                    interests: selected
                      ? preferences.interests.filter((id) => id !== focus.id)
                      : [...preferences.interests, focus.id],
                  })
                }
              >
                <span>
                  <strong>{copy[focus.title]}</strong>
                  <span>{copy[focus.description]}</span>
                </span>
                <span className="onboarding-choice-mark" aria-hidden="true">
                  {selected && <CheckIcon />}
                </span>
              </button>
            );
          })}
        </div>
      </section>
    </div>
  );
}

export function OnboardingFlow({
  context,
  firstName,
  onStarted,
}: {
  context: CompanyContext;
  firstName?: string;
  onStarted: (prompt?: string) => void;
}) {
  const { adopt } = useCompany();
  const copy = useNamespaceCopy(welcomeCopy);
  const company = context.company!;
  const key = setupKey(company.id, context.membership!.userId);
  const [preferences, setPreferences] = useState(() => readSetup(key));
  // After Continue on step 2 with nothing connected, the step offers the
  // suggestions once more in place (no blocking dialog).
  const [reminder, setReminder] = useState(false);
  const [pending, setPending] = useState<string | null>(null);
  const finishing = useRef(false);
  const [error, setError] = useState('');
  const heading = useRef<HTMLHeadingElement>(null);
  const active = useRef(true);
  useEffect(() => {
    active.current = true;
    return () => {
      active.current = false;
    };
  }, []);
  const connections = useConnections(company.name);
  const connected = connections.data?.items.filter((item) => item.status === 'connected') ?? [];
  const canSuggest = Boolean(
    !connections.error &&
    (connections.data?.pipedreamAvailable ||
      connections.data?.items.some((item) => item.actions.connect.available)),
  );
  const update = (patch: Partial<SetupPreferences>) => {
    const next = { ...preferences, ...patch };
    setPreferences(next);
    saveSetup(key, next);
    setReminder(false);
  };
  useEffect(() => {
    heading.current?.focus();
  }, [preferences.step, reminder]);
  const finish = async (prompt: string | undefined, id: string) => {
    if (finishing.current) return;
    finishing.current = true;
    setPending(id);
    setError('');
    try {
      const next = await completeCompanyWelcome();
      if (!active.current) return;
      // Choosing an example is the user's send action; the app submits it once
      // company admission and billing have resolved.
      onStarted(prompt);
      adopt(next);
    } catch {
      if (active.current) setError(copy.startFailed);
    } finally {
      finishing.current = false;
      if (active.current) setPending(null);
    }
  };
  const continueFromTools = () => {
    if (!reminder && connected.length === 0 && canSuggest) setReminder(true);
    else update({ step: 2 });
  };
  const busy = pending !== null;
  const waiting = Boolean(connections.busy);
  const step = preferences.step;
  const { title, intro } = stepText(copy, step, reminder, connected.length, firstName);
  const unavailable =
    step === 1 && !reminder && connections.data && !canSuggest && connected.length === 0;
  const skipLabel = reminder && !connected.length;
  return (
    <div className="onboarding-layout">
      <header className="onboarding-heading">
        <p className="onboarding-step">
          <span className="onboarding-company">{company.name}</span>
          <span aria-hidden="true"> · </span>
          <span>{formatCopy(copy.stepOf, { current: step + 1, total: STEPS })}</span>
        </p>
        <ol className="onboarding-progress" aria-label={copy.progressLabel}>
          {[copy.stepFocus, copy.stepTools, copy.stepFirst].map((label, index) => (
            <li
              key={label}
              aria-current={step === index ? 'step' : undefined}
              data-complete={step > index}
            >
              <span className="onboarding-progress-mark" aria-hidden="true" />
              <span className="onboarding-sr-only">{label}</span>
            </li>
          ))}
        </ol>
        <h1 ref={heading} tabIndex={-1}>
          {title}
        </h1>
        <p className="onboarding-intro">{intro}</p>
      </header>
      <div className="onboarding-stage" key={String(step) + ':' + reminder}>
        {step === 0 && <Profile copy={copy} preferences={preferences} update={update} />}
        {step === 1 &&
          (unavailable ? (
            <p className="onboarding-notice">
              <PlugIcon aria-hidden="true" />
              <span>{copy.toolsUnavailable}</span>
            </p>
          ) : (
            <ConnectTools
              key={String(reminder)}
              copy={copy}
              connections={connections}
              companyName={company.name}
              interests={preferences.interests}
              role={preferences.role}
              suggestion={reminder}
            />
          ))}
        {step === 2 && (
          <FirstStep
            connected={connected}
            busy={busy}
            pending={pending}
            copy={copy}
            onChoose={(prompt, id) => void finish(prompt, id)}
          />
        )}
      </div>
      {error && (
        <p className="onboarding-error" role="alert">
          {error}
        </p>
      )}
      <footer className="onboarding-footer">
        <div className="onboarding-footer-inner">
          {step > 0 || reminder ? (
            <EntryButton
              variant="quiet"
              disabled={busy || waiting}
              onClick={() => (reminder ? setReminder(false) : update({ step: step === 2 ? 1 : 0 }))}
            >
              <ArrowLeftIcon aria-hidden="true" />
              {copy.back}
            </EntryButton>
          ) : null}
          {step === 0 ? (
            <EntryButton onClick={() => update({ step: 1 })}>{copy.continue}</EntryButton>
          ) : step === 1 ? (
            <EntryButton
              variant={skipLabel ? 'secondary' : 'primary'}
              disabled={waiting}
              onClick={continueFromTools}
            >
              {skipLabel ? copy.continueWithout : copy.continue}
            </EntryButton>
          ) : (
            <EntryButton
              busy={pending === 'workspace'}
              busyLabel={copy.opening}
              disabled={busy}
              onClick={() => void finish(undefined, 'workspace')}
            >
              {copy.goToWorkspace}
            </EntryButton>
          )}
        </div>
      </footer>
    </div>
  );
}
